import React, { useEffect, useState } from 'react';
import styles from '../../assets/scss/InventoryManagement/PriceCategory.module.scss';
import Pagination from '../../Components/Common/Pagination';
import { useNavigate } from 'react-router-dom';
import {
  useGetOverallPricesQuery,
  useUpdateOverallPriceMutation
} from '../../slices/admin/admin';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { capitalize } from '../../utils/capitalize';

const PriceCategory = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [editableRows, setEditableRows] = useState({});

  const navigate = useNavigate();

  // debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, refetch } = useGetOverallPricesQuery(
    { search: debouncedSearch },
    { refetchOnMountOrArgChange: true }
  );

  const [updatePrice] = useUpdateOverallPriceMutation();

  const items = data?.products || [];
  const totalPages = Math.ceil(items.length / limit);
  const paginatedItems = items.slice((currentPage - 1) * limit, currentPage * limit);

  const handleClearAll = () => {
    setSearch('');
  };

  const handleCheckboxChange = (index, currentPrice) => {
    setEditableRows((prev) => ({
      ...prev,
      [index]: { checked: false, price: currentPrice?.toString() || '' },
    }));
  };

  const handlePriceChange = (index, value) => {
    setEditableRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        price: value,
      },
    }));
  };

  const handleSave = async (globalIndex) => {
    const pageIndex = globalIndex % limit;
    const item = paginatedItems[pageIndex];
    const edited = editableRows[globalIndex];

    if (edited?.price !== undefined && edited?.price !== '') {
      const newPrice = Number(edited.price);

      if (newPrice <= 0) {
        toast.error('Selling price must be greater than 0');
        return;
      }

      try {
        await updatePrice({
          productName: item.productName,
          overallPrice: newPrice,
          overrideOption: true,
        }).unwrap();

        toast.success(`✔ ${item.productName} price updated successfully!`);
        refetch();

        setEditableRows((prev) => {
          const updated = { ...prev };
          delete updated[globalIndex];
          return updated;
        });
      } catch (error) {
        toast.error(`✖ Failed to update ${item.productName}`);
      }
    }
  };

  // const handleCancel = async (productName, globalIndex) => {
  //   const itemIndex = items.findIndex((p) => p.productName === productName);
  //   const item = items[itemIndex];
  //   const edited = editableRows[globalIndex];

  //   // Always send the user-typed value (0 if empty)
  //   const newPrice =
  //     edited && edited.price !== undefined && edited.price !== ''
  //       ? Number(edited.price)
  //       : 0;

  //   try {
  //     await updatePrice({
  //       productName: item.productName,
  //       overallPrice: newPrice,
  //       overrideOption: false,
  //     }).unwrap();

  //     toast.success(`✖ ${item.productName} override cancelled.`);

  //     setEditableRows((prev) => {
  //       const updated = { ...prev };
  //       delete updated[globalIndex];
  //       return updated;
  //     });

  //     refetch();
  //   } catch (error) {
  //     toast.error(`⚠ Failed to cancel override for ${item.productName}`);
  //   }
  // };

  const handleCancel = async (productName, globalIndex) => {
  const itemIndex = items.findIndex((p) => p.productName === productName);
  const item = items[itemIndex];
  const edited = editableRows[globalIndex];

  const newPrice =
    edited && edited.price !== undefined && edited.price !== ''
      ? Number(edited.price)
      : 0;

  try {
    await updatePrice({
      productName: item.productName,
      overallPrice: 0,
      overrideOption: false,
    }).unwrap();

    toast.success(`✖ ${item.productName} override cancelled.`);

    setEditableRows((prev) => {
      const updated = { ...prev };
      delete updated[globalIndex];
      return updated;
    });

    refetch();
  } catch (error) {
    toast.error(`⚠ Failed to cancel override for ${item.productName}`);
  }
};


  return (
    <div className={styles.inventoryContainer}>
      <div className={styles.inventoryContainers}>
        {/* Search bar */}
        <div className={styles.filterInputs}>
          <input
            className={styles.searchInput}
            placeholder="Search here..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className={styles.clearBtn} onClick={handleClearAll}>
            Clear All
          </button>
        </div>

        {/* Table */}
        <div className={styles.tableWrapper}>
          <table>
            <thead>
              <tr>
                <th>Item Category</th>
                {/* <th>Total Count</th> */}
                <th>Total Quantity (kg)</th>
                <th>Selling Price</th>
                <th>Override Price</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item, idx) => {
                const globalIndex = (currentPage - 1) * limit + idx;
                const row = editableRows[globalIndex];
                const isEditable = row !== undefined;
                const inputVal = row?.price ?? '';
                const isSaveVisible = inputVal.toString().trim() !== '';

                return (
                  <tr key={idx}>
                    <td>{item.productName ? capitalize(item.productName) : '-'}</td>
                    {/* <td>{item.overallProductCount}</td> */}
                    <td>{item.overallQuantityKg} kg</td>
                    <td>
                      {isEditable ? (
                        <input
                          type="number"
                          placeholder="Enter new price"
                          value={inputVal}
                          className={styles.editableInput}
                          onChange={(e) =>
                            handlePriceChange(globalIndex, e.target.value)
                          }
                        />
                      ) : (
                        item.overallPrice ?? '-'
                      )}
                    </td>
                    <td>
                      {isEditable ? (
                        <div className={styles.actionBtns}>
                          {isSaveVisible && (
                            <button
                              className={`${styles.button} ${styles.save}`}
                              onClick={() => handleSave(globalIndex)}
                            >
                              Yes
                            </button>
                          )}
                          <button
                            className={`${styles.button} ${styles.cancel}`}
                            onClick={() => handleCancel(item.productName, globalIndex)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <input
                          type="checkbox"
                          checked={item.overrideOption || false}
                          onChange={() =>
                            handleCheckboxChange(globalIndex, item.overallPrice)
                          }
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && paginatedItems.length === 0 && (
                <tr>
                  <td colSpan="5" className={styles.noData} style={{ textAlign: 'center' }}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
          <select
            className={styles.inputsbtn}
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={5}>5 rows</option>
            <option value={10}>10 rows</option>
            <option value={25}>25 rows</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default PriceCategory;


